import type { AxiosInstance } from 'axios'
import { captureException } from '@sentry/nuxt'
import type { Notifier, TokenManager, LoggerFactory } from '~/shared/interfaces'
import { createLogger } from '~/shared/utils/logger'
import { RequestService } from './RequestService'
import { RequestRestService } from './RequestRestService'
import { RegionsService } from './RegionsService'
import { SearchService } from './SearchService'
import { EcosystemService } from './EcosystemService'
import { ArticleService } from './articles'
import { DigitalLectureService } from './digital-lecture'
import { EmployerService } from './employer'
import { UserService } from '~/services/user/UserService'
import { EmployersTaskService } from '~/services/employers-tasks/EmployersTaskService'
import { WorkCardsService } from '~/services/WorkCardsService'
import { CountDataService } from '~/services/CountDataService'
import { StudentActivitiesService } from '~/services/student-activities/StudentActivitiesService'
import { SubscriptionsService } from '~/services/subscriptions/SubscriptionsService'
import { CareerRoutesService } from '~/services/career-routes'
import { ResearchShowcaseService } from '~/services/research-showcase'
import { ResearchDemandService } from '~/services/research-demand'
import { VacancyService } from '~/services/vacancy'
import { BreadcrumbsService } from './BreadcrumbsService'
import { VacanciesService } from '~/services/vacancies'
import { PageSeoService } from './PageSeoService'
import { ResumesService } from '~/services/resumes'
import { FeedbacksService } from '~/services/feedbacks'
import { AgreementsService } from '~/services/agreements'
import { InternshipService } from '~/services/internship'
import { AgrocityService } from '~/services/agrocity'
import { UniversitiesService } from '~/services/universities'
import { LandingsService } from '~/services/landings'
import { DadataService } from '~/services/dadata'

export interface Services {
  request: RequestService
  requestRest: RequestRestService
  pageSeoService: PageSeoService
  breadcrumbsService: BreadcrumbsService
  regionsService: RegionsService
  searchService: SearchService
  ecosystemService: EcosystemService
  userService: UserService
  articleService: ArticleService
  digitalLectureService: DigitalLectureService
  employerService: EmployerService
  employersTaskService: EmployersTaskService
  workCardsService: WorkCardsService
  countDataService: CountDataService
  dadataService: DadataService
  studentActivitiesService: StudentActivitiesService
  careerRoutesService: CareerRoutesService
  vacanciesService: VacanciesService
  subscriptionsService: SubscriptionsService
  researchShowcaseService: ResearchShowcaseService
  researchDemandService: ResearchDemandService
  vacancyService: VacancyService
  resumesService: ResumesService
  feedbacksService: FeedbacksService
  agreementsService: AgreementsService
  internshipService: InternshipService
  agrocityService: AgrocityService
  universitiesService: UniversitiesService
  landingsService: LandingsService
}

export interface ServicesDependencies {
  axiosInstance: AxiosInstance
  notifier: Notifier
  tokenManager: TokenManager
  authHost: string
  dadataBaseUri: string
}

export function initializeServices({
  axiosInstance,
  notifier,
  tokenManager,
  authHost,
  dadataBaseUri,
}: ServicesDependencies): Services {
  const loggerFactory: LoggerFactory = scope => createLogger({ scope, captureException })

  const request = new RequestService(axiosInstance, notifier)
  const requestRest = new RequestRestService(request)
  const pageSeoService = new PageSeoService(request)
  const breadcrumbsService = new BreadcrumbsService(request)
  const regionsService = new RegionsService(request)
  const searchService = new SearchService(request)
  const ecosystemService = new EcosystemService(request)
  const userService = new UserService(request, tokenManager, authHost, loggerFactory('user'))
  const articleService = new ArticleService(requestRest, request, loggerFactory)
  const digitalLectureService = new DigitalLectureService(request, loggerFactory)
  const employerService = new EmployerService(request)
  const employersTaskService = new EmployersTaskService(request)
  const workCardsService = new WorkCardsService(request)
  const countDataService = new CountDataService(request)
  const dadataService = new DadataService(request, dadataBaseUri)
  const studentActivitiesService = new StudentActivitiesService(request)
  const careerRoutesService = new CareerRoutesService(request)
  const vacanciesService = new VacanciesService(requestRest, request)
  const subscriptionsService = new SubscriptionsService(request)
  const researchShowcaseService = new ResearchShowcaseService(request, loggerFactory)
  const researchDemandService = new ResearchDemandService(request, loggerFactory('ResearchDemandService'))
  const vacancyService = new VacancyService(request)
  const resumesService = new ResumesService(request, loggerFactory)
  const feedbacksService = new FeedbacksService(requestRest, request, loggerFactory)
  const agreementsService = new AgreementsService(request)
  const internshipService = new InternshipService(request, requestRest)
  const agrocityService = new AgrocityService(request)
  const universitiesService = new UniversitiesService(request)
  const landingsService = new LandingsService(request)

  return {
    request,
    requestRest,
    pageSeoService,
    breadcrumbsService,
    regionsService,
    searchService,
    ecosystemService,
    userService,
    articleService,
    digitalLectureService,
    employerService,
    employersTaskService,
    workCardsService,
    countDataService,
    dadataService,
    studentActivitiesService,
    careerRoutesService,
    vacanciesService,
    subscriptionsService,
    researchShowcaseService,
    researchDemandService,
    vacancyService,
    resumesService,
    feedbacksService,
    agreementsService,
    internshipService,
    agrocityService,
    universitiesService,
    landingsService,
  }
}
